/*
 * Dubai Looka — urgency widgets.
 *
 * <countdown-timer> supports two modes:
 *
 *   real    — counts down to an absolute epoch the owner set. Same deadline for
 *             everyone. Removes itself when the clock passes it, which also
 *             covers a stale render served from Shopify's full-page cache.
 *
 *   rolling — an "evergreen" deadline generated per visitor, restarting every N
 *             hours. NOT tied to a real price change. Persisted in localStorage
 *             per product so it does not reset on reload; two visitors will see
 *             different times remaining.
 *
 * <viewer-count> renders a generated number. Shopify exposes no concurrent
 * visitor data to a theme, so there is nothing real to read. It is seeded from
 * the product id and the current hour so it stays put across reloads within an
 * hour instead of jumping on every page load, then drifts by one occasionally.
 *
 * Both write textContent on pre-rendered nodes. No innerHTML, no layout thrash,
 * one interval each, cleared on disconnect.
 */

function dlSafeStorage(action, key, value) {
  /* Safari private mode throws on write; never let that abort the script. */
  try {
    if (action === 'get') return window.localStorage.getItem(key);
    window.localStorage.setItem(key, value);
  } catch (error) {
    return null;
  }
  return null;
}

if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      connectedCallback() {
        this.endsAt = this.dataset.mode === 'real' ? this.realDeadline() : this.rollingDeadline();

        if (!this.endsAt || Number.isNaN(this.endsAt)) {
          this.removeSelf();
          return;
        }

        this.parts = {
          days: this.querySelector('[data-countdown-days]'),
          hours: this.querySelector('[data-countdown-hours]'),
          minutes: this.querySelector('[data-countdown-minutes]'),
          seconds: this.querySelector('[data-countdown-seconds]'),
        };

        this.tick();
        this.interval = setInterval(this.tick.bind(this), 1000);
      }

      disconnectedCallback() {
        if (this.interval) clearInterval(this.interval);
      }

      realDeadline() {
        return parseInt(this.dataset.ends, 10) * 1000;
      }

      rollingDeadline() {
        const hours = parseFloat(this.dataset.rollingHours) || 12;
        const storageKey = `dl-countdown-${this.dataset.key}`;
        const stored = parseInt(dlSafeStorage('get', storageKey), 10);

        /* Reuse the stored deadline while it is still ahead of us, so a reload
           continues the same countdown rather than restarting it. */
        if (stored && stored > Date.now()) return stored;

        const next = Date.now() + hours * 3600000;
        dlSafeStorage('set', storageKey, String(next));
        return next;
      }

      tick() {
        const remaining = this.endsAt - Date.now();

        if (remaining <= 0) {
          if (this.dataset.mode === 'real') {
            this.removeSelf();
          } else {
            /* Rolling timers restart rather than disappear. */
            this.endsAt = this.rollingDeadline();
          }
          return;
        }

        const totalSeconds = Math.floor(remaining / 1000);
        this.write('days', Math.floor(totalSeconds / 86400));
        this.write('hours', Math.floor(totalSeconds / 3600) % 24);
        this.write('minutes', Math.floor(totalSeconds / 60) % 60);
        this.write('seconds', totalSeconds % 60);
      }

      write(unit, value) {
        const node = this.parts[unit];
        if (!node) return;
        const text = value < 10 ? `0${value}` : String(value);
        if (node.textContent !== text) node.textContent = text;
      }

      removeSelf() {
        if (this.interval) clearInterval(this.interval);
        const wrapper = this.closest('.urgency');
        /* Only take the whole block down if nothing else lives in it. */
        if (wrapper && wrapper.children.length <= 1) {
          wrapper.remove();
        } else {
          this.remove();
        }
      }
    }
  );
}

if (!customElements.get('viewer-count')) {
  customElements.define(
    'viewer-count',
    class ViewerCount extends HTMLElement {
      connectedCallback() {
        this.min = parseInt(this.dataset.min, 10) || 8;
        this.max = parseInt(this.dataset.max, 10) || 60;
        if (this.max < this.min) this.max = this.min;

        this.output = this.querySelector('[data-viewer-count]');
        if (!this.output) return;

        this.value = this.seed();
        this.render();
        this.hidden = false;

        this.interval = setInterval(this.drift.bind(this), 9000);
      }

      disconnectedCallback() {
        if (this.interval) clearInterval(this.interval);
      }

      /* Deterministic per product per hour: the same visitor reloading the page
         sees the same number, instead of it jumping about on every load. */
      seed() {
        const hourBucket = Math.floor(Date.now() / 3600000);
        const raw = String(this.dataset.key || '') + ':' + hourBucket;
        let hash = 0;
        for (let i = 0; i < raw.length; i += 1) {
          hash = (hash * 31 + raw.charCodeAt(i)) % 100000;
        }
        return this.min + (hash % (this.max - this.min + 1));
      }

      drift() {
        const step = Math.random() < 0.5 ? -1 : 1;
        const next = this.value + step;
        if (next < this.min || next > this.max) return;
        this.value = next;
        this.render();
      }

      render() {
        const text = String(this.value);
        if (this.output.textContent !== text) this.output.textContent = text;
      }
    }
  );
}
